import React, { useCallback } from 'react';
import { Dispatch } from 'redux';
import { history } from '../../../../store/appStore';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { GIButton } from 'app-components';
import endpoints from '../../../../constants/configs/endpoints';
import { useAppSelector, useAppDispatch } from '../../../../hooks';
import { IUserInfo } from '../../types/ridpTypes';
import { StyledIdentityHeader } from '../Layout';
import AlertMessage from '../../../../components/individualPortal/dashboard/AlertMsgs/AlertMessage';
import { clearDHSReferenceData, validateFARSData } from '../../../../actions/ridp/ridpActionCreators';
import { useHandleValidateFARSResponse } from '../../utils/useHandleRidpResponse';
import { ResponseCodes } from '../../types/responseCodes';
import { environmentSelector } from '../../../../selectors/commonSelectors';
import { ConditionalRendering } from '../../../../components/common/conditional-rendering';

const StyledCallExperianContainer = styled.div`
    .gi-alert {
        padding: 10px;
        margin-top: 20px !important;
        margin-bottom: 20px !important;
    }
    .gi-alert__info {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 20px;

        .gi-alert__info__text {
            > span {
                color: ${({ theme }) => theme.dangerDark};
                font-weight: bold;
            }
        }
    }
    .reference-number-error {
        > div > div > div > span > a {
            color: ${({ theme }) => theme.primaryMain};
            cursor: pointer;
            &:hover {
                color: ${({ theme }) => theme.primaryMain};
            }
        }
        div {
            margin-top: 0px !important;
        }
    }
`;

const StyledContent = styled.p`
    font-size: 16px;
    padding-left: 10px;
`;

const StyledParagraphDescription = styled.p`
    font-size: 16px;
    color: ${({ theme }) => theme.gray2};
    font-weight: 400;
`;

const StyledButton = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
    padding-right: 25px;
    padding-bottom: 25px;

    > button {
        text-transform: none;
        background-color: ${({ theme }) => theme.primaryMain};
    }
`;

const CallExperian = () => {
    const dispatch: Dispatch<any> = useAppDispatch();
    const currentUserInfo = useAppSelector(state => state.ridp.currentUserInfo) as IUserInfo;
    const ridpValidateFARSResponse = useAppSelector(state => state.ridp.ridpValidateFARSResponse);
    const viewRef = useAppSelector(state => state.ridp.viewRef);
    const environment = useAppSelector(environmentSelector);
        const existingParams = new URLSearchParams(window.location.search);

    const [errorMessage, setErrorMessage] = React.useState<string>('');

    const validateFARS = useCallback(
        e => {
            const payload = {
                dshReferenceNumber: currentUserInfo?.dhsReferenceNumber,
            };
            e.preventDefault();
            dispatch(validateFARSData(endpoints.RIDP_VALIDATE_FARS_ENDPOINT, payload));
        },
        [currentUserInfo],
    );

    useHandleValidateFARSResponse(ridpValidateFARSResponse, setErrorMessage);

    const clearDHSReference = useCallback(() => {
        dispatch(
            clearDHSReferenceData(
                endpoints.RIDP_CLEAR_DHS_ENDPOINT,
                viewRef,
                environment,
                history,
                existingParams,
                setErrorMessage,
                ridpValidateFARSResponse,
            ),
        );
    }, [ridpValidateFARSResponse, viewRef, environment, history, existingParams, setErrorMessage, endpoints]);

    return (
        <StyledCallExperianContainer>
            <StyledIdentityHeader>
                <FormattedMessage id="ridp.sidebar.callExperian" />
            </StyledIdentityHeader>
            <AlertMessage
                showIcon={false}
                content={<FormattedMessage id="ridp.callExperian.alert.para" />}
                type="error"
            />
            <StyledContent>
                <StyledParagraphDescription>
                    <FormattedMessage id="ridp.callExperian.description.para1" />
                </StyledParagraphDescription>
                <StyledParagraphDescription>
                    <FormattedMessage
                        id="ridp.callExperian.description.para2"
                        values={{ serviceContactName: currentUserInfo?.serviceContactName }}
                    />
                </StyledParagraphDescription>
                <StyledParagraphDescription>
                    <FormattedMessage
                        id="ridp.callExperian.description.para3"
                        values={{ serviceContactCallCenterNumber: currentUserInfo?.serviceContactCallCenterNumber }}
                    />
                </StyledParagraphDescription>
                <StyledParagraphDescription>
                    <FormattedMessage id="ridp.callExperian.description.para4" />
                </StyledParagraphDescription>

                <AlertMessage
                    showIcon={false}
                    content={
                        <>
                            <FormattedMessage id="ridp.callExperian.description.alerinfo" />
                            <span>{currentUserInfo?.dhsReferenceNumber}</span>
                        </>
                    }
                    type="info"
                />

                <ConditionalRendering isTrue={errorMessage !== ''}>
                    <AlertMessage
                        showIcon={false}
                        content={
                            <>
                                <FormattedMessage
                                    id={errorMessage}
                                    values={{ serviceContactName: currentUserInfo.serviceContactName }}
                                />
                                <ConditionalRendering
                                    isTrue={
                                        ridpValidateFARSResponse?.ResponseMetadata?.ResponseCode ===
                                        ResponseCodes.HE200006
                                    }
                                >
                                    <span>
                                        <FormattedMessage id="ridp.callExperian.referencenum.errorMsg1" />{' '}
                                        <a onClick={() => clearDHSReference()}>
                                            {' '}
                                            <FormattedMessage id="ridp.callExperian.referencenum.errorMsg2" />
                                        </a>{' '}
                                        <FormattedMessage id="ridp.callExperian.referencenum.errorMsg3" />
                                    </span>
                                </ConditionalRendering>
                                <ConditionalRendering
                                    isTrue={
                                        ridpValidateFARSResponse?.ResponseMetadata?.ResponseCode ===
                                        ResponseCodes.HE200045
                                    }
                                >
                                    <span>
                                        <FormattedMessage id="ridp.callExperian.serviceContact.errorMsg1" />{' '}
                                        <a onClick={() => clearDHSReference()}>
                                            {' '}
                                            <FormattedMessage id="ridp.callExperian.referencenum.errorMsg2" />
                                        </a>
                                    </span>
                                </ConditionalRendering>
                            </>
                        }
                        type="error"
                        className="reference-number-error"
                    />
                </ConditionalRendering>

                <StyledButton>
                    <GIButton
                        type="submit"
                        id="ridpCallExperian"
                        labelId="ridp.callExperian.button.label"
                        onClickHandler={e => validateFARS(e)}
                    />
                </StyledButton>
            </StyledContent>
        </StyledCallExperianContainer>
    );
};

export default CallExperian;
